import React, { useState } from "react";
import { TextInputModeEnum } from "@brewbase/db/types";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { EditIcon, PlusIcon, TrashIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import InlineEditableTextarea from "../InlineEditableTextarea";

interface TextFieldsValue {
  text_input_mode?: TextInputModeEnum | null;
  text_instructions?: string | null;
  text_options?: unknown;
}

interface TextFieldsEditorProps {
  value: TextFieldsValue;
  onChange: (value: TextFieldsValue) => void;
}

const textInputModeOptions = [
  {
    value: TextInputModeEnum.free_text,
    label: "Free Text",
    description: "Open text input",
  },
  {
    value: TextInputModeEnum.dropdown,
    label: "Dropdown",
    description: "Fixed set of options",
  },
  {
    value: TextInputModeEnum.autocomplete,
    label: "Autocomplete",
    description: "Suggests previous values",
  },
];

export function TextFieldsEditor({ value, onChange }: TextFieldsEditorProps) {
  const [tempOptions, setTempOptions] = useState<string[]>([]);

  const handleInputModeChange = (newMode: TextInputModeEnum) => {
    const updates: TextFieldsValue = {
      ...value,
      text_input_mode: newMode,
    };

    // Clear options if not dropdown mode
    if (newMode !== TextInputModeEnum.dropdown) {
      updates.text_options = null;
    }

    onChange(updates);
  };

  const [editingOptions, setEditingOptions] = useState<boolean>(false);
  const handleOptionsEdit = () => {
    const currentOptions = Array.isArray(value.text_options)
      ? value.text_options
      : [];
    setTempOptions([...currentOptions]);
    setEditingOptions(true);
  };

  const addOption = () => {
    setTempOptions([...tempOptions, ""]);
  };

  const updateOption = (index: number, newValue: string) => {
    const newOptions = [...tempOptions];
    newOptions[index] = newValue;
    setTempOptions(newOptions);
  };

  const removeOption = (index: number) => {
    const newOptions = tempOptions.filter((_, i) => i !== index);
    setTempOptions(newOptions);
  };

  const getOptionsDisplayValue = () => {
    if (!Array.isArray(value.text_options) || value.text_options.length === 0) {
      return "No options defined";
    }
    return `${value.text_options.length} option(s): ${value.text_options.slice(0, 3).join(", ")}${value.text_options.length > 3 ? "..." : ""}`;
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-foreground">Text Settings</h4>

      {/* Input Mode */}
      <div className="space-y-1">
        <Label className="text-xs font-medium">Input Mode</Label>
        <Select
          value={(value.text_input_mode as any) || ""}
          onValueChange={(val) =>
            handleInputModeChange(val as TextInputModeEnum)
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select input mode..." />
          </SelectTrigger>
          <SelectContent>
            {textInputModeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <div>
                  <div className="font-medium">{option.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {option.description}
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Options Editor (only for dropdown mode) */}
      {value.text_input_mode === TextInputModeEnum.dropdown && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label className="text-xs font-medium">Options</Label>
            {editingOptions && (
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    onChange({ ...value, text_options: tempOptions });
                    setEditingOptions(false);
                  }}
                  className="h-6 text-xs"
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingOptions(false);
                  }}
                  className="h-6 text-xs"
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>

          {editingOptions ? (
            <>
              <div className="w-64 flex-shrink-0">
                <div className="space-y-1 p-2 max-h-32 overflow-y-auto">
                  {tempOptions.map((option, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <Input
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        className="p-1 h-6 text-xs flex-1"
                        inputClassName="p-1 pl-2 h-5 text-xs"
                        data-option-index={index}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();

                            if (
                              index === tempOptions.length - 1 &&
                              (e.target as HTMLInputElement).value.trim() !== ""
                            )
                              addOption();

                            // Focus the next input
                            setTimeout(() => {
                              const nextInput = document.querySelector(
                                `input[data-option-index="${index + 1}"]`,
                              ) as HTMLInputElement;
                              if (nextInput) {
                                nextInput.focus();
                              }
                            }, 50);
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeOption(index)}
                        className="h-6 w-6 p-0 text-error hover:text-error-foreground hover:bg-error"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={addOption}
                    className="h-6 text-xs text-muted-foreground hover:text-foreground w-full"
                  >
                    <PlusIcon className="h-3 w-3 mr-1" />
                    Add Option
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div
                className="col-span-2 text-sm cursor-pointer hover:bg-muted rounded px-2 py-1 min-h-[24px] flex items-center"
                onClick={handleOptionsEdit}
              >
                {getOptionsDisplayValue()}
              </div>
            </>
          )}
        </div>
      )}

      {/* Instructions */}
      <div className="space-y-1">
        <Label className="text-xs font-medium">Instructions</Label>
        <InlineEditableTextarea
          value={(value.text_instructions as string) || ""}
          onSave={(newVal) =>
            onChange({
              ...value,
              text_instructions: newVal,
            })
          }
          placeholder="Enter instructions for users..."
          className="h-16 text-xs resize-none"
        />
      </div>
    </div>
  );
}
